import { REQUIRED_MESSAGES } from '@srm/ui/lib/requiredMessage';
import React, { useEffect, useMemo, useState } from 'react';
import { IAddressBookData } from '@models/addressbook/IAddressBookData';
import { IAbookOptions } from '@models/Forms/IAbookOptions';
import { ActivityExecutionService } from '@services/actions/ActivityExecutionService';
import { IFormValues } from '@models/Forms/IForms';
import { getABookFormValues } from '@utils/abookUtils';
import { useForm } from 'react-hook-form';
import ModalContainer, { IModalContainerProps } from '@molecules/ModalContainer/ModalContainer';
import { useActionSubmit } from '@organisms/actions/useActionSubmit';
import { useUnsavedFormInput } from '@srm/ui/hooks/useUnsavedFormInput';
import { IChangePerformerFormData } from './IChangePerformerFormData';
import { IChangePerformerProps } from './IChangePerformerProps';
import AbookpickerControl from '@controls/AbookpickerControl';
import SelectControl from '@srm/ui/controls/SelectControl';
import TextareaControl from '@srm/ui/controls/TextareaControl';
import { declensionOfSymbol } from '@utils/helpers';
import { requiredTextRules } from '@srm/ui/lib/requiredTextRules';
import { useDocumentAbookTabs } from '@/hooks/useDocumentAbookTabs';

export const ChangePerformerModal: React.FC<IChangePerformerProps> = (props: IChangePerformerProps) => {
    const [executor, setExecutor] = useState<IAddressBookData>();
    const [abookOptions, setAbookOptions] = useState<IAbookOptions>();
    const abookTabs = useDocumentAbookTabs(props.docId, abookOptions?.tabs);
    const [formValues, setFormValues] = useState<string>('{}');
    const activated = React.useRef(false);

    const serv = new ActivityExecutionService();

    const { errorText, setErrorText, isBusy, setIsBusy, isSubmitting, okClick, cancelClick } = useActionSubmit({
        completeHandler: props.completeHandler,
        initialBusy: true,
        submit: (form: IChangePerformerFormData, reason: string) =>
            serv.saveChangePerformer(props.actionKey, props.activityId, form.executor, reason),
    });

    const getFormValues = async (fValues?: IFormValues) => {
        const vals = await getABookFormValues(props.docId, fValues);
        if (activated.current && vals) {
            setFormValues(vals);
        }
    };

    const onSubmit = (form: IChangePerformerFormData) => {
        if (!form?.executor) {
            setErrorText('Не указан исполнитель');
            return;
        }

        let reason = '';
        if (props.reasons.length > 0 && form.reason?.length) {
            const selectedItem = form.reason[0].value;
            if (selectedItem === -1) {
                reason = form.customReason || 'Прочее';
            } else {
                reason = props.reasons[selectedItem];
            }
        } else if (props.addCustomReason) {
            reason = form.customReason;
        }

        return okClick(form, reason);
    };

    const formState = useForm<IChangePerformerFormData>({
        mode: 'onBlur', // "onChange"
    });
    const hasUnsavedInput = useUnsavedFormInput(formState, { textFields: ['customReason'] });

    useEffect(() => {
        // Закрытое окно ответ подготовки не применяет.
        let ignore = false;
        setIsBusy(true);
        activated.current = true;

        serv.getChangePerformerModel(props.actionKey, props.activityId)
            .then(async (res) => {
                if (ignore) return;
                setAbookOptions(res.data);
                await getFormValues(res.data?.formValues);
            })
            .catch(() => {
                if (!ignore) setErrorText('Ошибка получения данных от сервера.');
            })
            .finally(() => {
                if (!ignore) setIsBusy(false);
            });

        return () => {
            ignore = true;
            activated.current = false;
        };
    }, []);

    // -------------------------------------------------------------------------------------------------------------------

    const [showCustomReason, setShowCustomReason] = useState<boolean>(
        props.reasons.length === 0 && props.addCustomReason,
    );

    const reasonsOptions = useMemo(() => {
        const options = props.reasons.map((reason, index) => {
            return { label: reason, value: index };
        });

        if (props.reasons.length > 0 && props.addCustomReason) {
            options.push({ label: 'Прочее', value: -1 });
        }

        return options;
    }, [props.reasons, props.addCustomReason]);

    // -------------------------------------------------------------------------------------------------------------------

    const modalContainerProps: IModalContainerProps = {
        header: props.title,
        size: props.modalSize,
        showOkButton: true,
        showCancelButton: true,
        okButtonText: props.okButtonText,
        cancelButtonText: props.cancelButtonText,
        loading: isBusy,
        closeDisabled: isSubmitting,
        hasUnsavedInput,
        onCancel: cancelClick,
        errorText: errorText,
        formMethods: formState,
        onSubmit: onSubmit,
        okButtonDisabled: false,
    };

    return (
        <ModalContainer {...modalContainerProps}>
            <AbookpickerControl
                label="Исполнитель"
                placeholder="Исполнитель"
                isMultiple={false}
                title="Исполнитель"
                displayValue={executor?.fullName ?? ''}
                formValues={formValues}
                name="executor"
                tabsSettings={abookTabs}
                externalSearch={abookOptions?.externalSearch}
                required
                rules={{ required: REQUIRED_MESSAGES.select }}
                control={formState.control}
                showChips={false}
                onSetValues={(value: IAddressBookData[]) => {
                    setExecutor(value[0]);
                }}
            />
            {reasonsOptions.length > 0 ? (
                <SelectControl
                    label="Причина"
                    multiselect={false}
                    readOnly
                    required={true}
                    rules={{ required: REQUIRED_MESSAGES.select }}
                    name="reason"
                    options={reasonsOptions}
                    loading={isBusy}
                    control={formState.control}
                    defaultValue={[reasonsOptions[0]]}
                    onValueChange={(value) => {
                        setShowCustomReason(value[0].value === -1);
                    }}
                />
            ) : null}
            {showCustomReason ? (
                <TextareaControl
                    label={props.customReasonLabel}
                    rows={6}
                    readOnly={false}
                    required={props.customReasonRequired}
                    rules={{
                        ...(props.customReasonRequired ? requiredTextRules() : {}),
                        minLength: {
                            value: props.customReasonMinLength,
                            message: `Минимальная длина комментария: ${props.customReasonMinLength} ${declensionOfSymbol(Number(props.customReasonMinLength))}`,
                        },
                    }}
                    name="customReason"
                    control={formState.control}
                />
            ) : null}
        </ModalContainer>
    );
};
